import React, { forwardRef } from 'react';
import { AddingVisitorsBlock } from 'components/adding-visitors-block';
import { Alert } from 'components/alert';
import { Button } from '../button/button';
import { CheckoutTermsAndConditions } from 'components/payment/quick-checkout/components/terms-and-conditions';
import { CloseButton } from 'components/close-button';
import { PaymentMethodsSection } from 'components/payment/quick-checkout/components/payment-methods-section';
import { formatDate } from 'utils/formatDate';
import { VisitorsError } from 'components/adding-visitors-block/visitors-error.enum';
import {
  CloseButtonContainer,
  ContentContainer,
  EditButton,
  QuickCheckoutContent,
  StyledPriceBlock,
  StyledTitleBlock,
} from './styles';
import { useIntl } from 'react-intl';
import { SavedPaymentMethod } from 'store/payment/types';
import { BookingConfirmationModalBaseProps } from 'components/payment/quick-checkout/components/types';
import { ItemBlock } from './components/item-block';

export interface BookingConfirmationModalProps extends BookingConfirmationModalBaseProps {
  isProcessPaymentFailed: boolean;
  isVisiblePaymentMethodBlock: boolean;
  isAllowedVisitorsExist: boolean;
  hasInvoiceWithoutCreditCard: boolean;
  isNativePaymentError: boolean;
  pendingNativePayment: boolean;
  currentPaymentMethodId: string;
  visitorError?: VisitorsError;
  paymentMethod?: SavedPaymentMethod;
  isFreeCart: boolean;
  isPaymentMethodsAvailable: boolean;
  onEditClick: VoidFunction;
  onAddNewCardClick: VoidFunction;
}

const BookingConfirmationModal = forwardRef<HTMLDivElement, BookingConfirmationModalProps>(
  function BookingConfirmationModalComponent(
    {
      checkoutDisabled,
      closeQuickCheckoutHandler,
      isProcessPaymentFailed,
      onCTAClick,
      onEditClick,
      onAddNewCardClick,
      cart,
      resourceTimezone,
      isVisiblePaymentMethodBlock,
      locale,
      isAllowedVisitorsExist,
      isNativePaymentError,
      pendingNativePayment,
      isTermsVisible,
      isTermsChecked,
      cartError,
      currentPaymentMethodId,
      visitorError,
      hasInvoiceWithoutCreditCard,
      paymentMethod,
      isFreeCart,
      isPaymentMethodsAvailable,
      onIsTermsChecked,
      submitCartStatus,
      autoConfirm,
    },
    ref,
  ) {
    const intl = useIntl();

    const { eventWeekDay, eventDateMonthLong, formatEventStartTime, eventDateDay } = formatDate(
      cart?.items[0]?.resource_booking?.start_at,
      locale as string,
      resourceTimezone,
    );

    return (
      <ContentContainer data-testid="booking-confirmation-modal">
        <CloseButtonContainer>
          <CloseButton onClose={closeQuickCheckoutHandler} />
        </CloseButtonContainer>
        <QuickCheckoutContent className="quick-checkout-content" ref={ref}>
          <StyledTitleBlock
            spacing={20}
            title={intl.formatMessage({ id: 'book' })}
            subtitle={intl.formatMessage(
              { id: 'you_are_about_book' },
              {
                serviceName: cart.items?.[0]?.display_info.title,
                serviceTime: `${eventWeekDay}, ${eventDateMonthLong} ${eventDateDay}, ${intl.formatMessage({
                  id: 'at',
                })} ${formatEventStartTime}`,
              },
            )}
          />
          <StyledPriceBlock>
            {cart.items?.map(item => (
              <ItemBlock key={item.id} cart={cart} item={item} isFreeCart={isFreeCart} locale={locale} />
            ))}
          </StyledPriceBlock>
          {isAllowedVisitorsExist && <AddingVisitorsBlock />}
          <PaymentMethodsSection
            isAllowedVisitorsExist={isAllowedVisitorsExist}
            isVisiblePaymentMethodBlock={isVisiblePaymentMethodBlock}
            onEditClick={onEditClick}
            cart={cart}
            isProcessPaymentFailed={isProcessPaymentFailed}
            currentPaymentMethodId={currentPaymentMethodId}
            paymentMethod={paymentMethod}
            cartError={cartError}
            isNativePaymentError={isNativePaymentError}
          />
          {hasInvoiceWithoutCreditCard && (
            <EditButton data-testid="add-cards-option" onClick={onAddNewCardClick}>
              {intl.formatMessage({ id: 'add_new_card' })}
            </EditButton>
          )}
        </QuickCheckoutContent>
        {isTermsVisible && (
          <CheckoutTermsAndConditions termsChecked={isTermsChecked} setIsTermsChecked={onIsTermsChecked} />
        )}
        {Boolean(visitorError) && visitorError !== VisitorsError.INVALID_EMAIL && (
          <Alert label={visitorError} testId="visitor-error-alert" />
        )}
        <Button
          isPaymentMethodsAvailable={isPaymentMethodsAvailable}
          isFreeCart={isFreeCart}
          submitCartStatus={submitCartStatus}
          onCTAClick={onCTAClick}
          pendingNativeMethod={pendingNativePayment}
          checkoutDisabled={checkoutDisabled}
          autoConfirm={autoConfirm}
        />
      </ContentContainer>
    );
  },
);

export default BookingConfirmationModal;
